"use client";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  CopyAll as CopyIcon,
  HttpRounded as HTTPIcon
} from "@mui/icons-material";
import JSIcon from "mdi-react/LanguageJavascriptIcon";
import PyIcon from "mdi-react/LanguagePythonIcon";

import { useStore, observer } from "../../service/mobx";
import defaultInputs from "../../view/model/Content/Play/InputBox/defaultInputs.json";

const VsCode = dynamic(() => import("../../component/Code/VsCode"));

function ModelCode() {
  const [language, setLanguage] = usePreferredLanguage();
  const [code, highlight] = useCode(language);
  const { model, snackbar } = useStore();

  return (
    <Box
      p={{ compact: 0, expanded: 1 }}
      overflow="hidden"
      borderRadius="var(--shape-sm)"
      bgcolor="var(--dark-surface-container-lowest)"
    >
      <Stack direction="row" justifyContent="space-between" spacing={1} pb={1}>
        <ToggleButtonGroup
          exclusive
          value={language}
          color="warning"
          onChange={(_, language) => {
            const newLanguage = language || "javascript";

            setLanguage(newLanguage);
          }}
        >
          <ToggleButton value="python">
            <PyIcon
              size={24}
              color={
                language === "python"
                  ? undefined
                  : "var(--dark-surface-on-color)"
              }
            />
          </ToggleButton>
          <ToggleButton value="javascript">
            <JSIcon
              size={24}
              color={
                language === "javascript"
                  ? undefined
                  : "var(--dark-surface-on-color)"
              }
            />
          </ToggleButton>
          <ToggleButton
            value="http"
            target="_blank"
            href={`https://docs.bytez.com/http-reference/examples/open-source/${model.node.task}/${model.node.task}`}
          >
            <HTTPIcon
              size={24}
              sx={{
                color:
                  language === "http"
                    ? undefined
                    : "var(--dark-surface-on-color)"
              }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
        <IconButton
          onClick={() => {
            navigator.clipboard.writeText(code);
            snackbar.notify({ text: "Copied" });
          }}
        >
          <CopyIcon sx={{ color: "var(--dark-surface-on-variant)" }} />
        </IconButton>
      </Stack>
      <VsCode
        key={language + highlight.length}
        readOnly
        value={code}
        highlight={highlight}
        language={language}
      />
    </Box>
  );
}

export default observer(ModelCode);

function usePreferredLanguage() {
  const [language, setLanguage] = useState("");
  const { utilities } = useStore();

  useEffect(() => {
    setLanguage(utilities.cookie.get("lang") || "javascript");
  }, [utilities]);

  useEffect(() => {
    // remember preferred language
    utilities.cookie.set("lang", language);
  }, [utilities, language]);

  useEffect(() => {
    if (language === "http") {
      setLanguage("python");
    }
  }, [language]);

  return [language, setLanguage];
}

function useCode(language) {
  const [highlight, setHighlight] = useState([9, 10, 12]);
  const [code, setCode] = useState("");
  const { user, model } = useStore();

  useEffect(() => {
    const key = user.key || "your-free-key";
    const { task, modelId, displayName, closed } = model.node;
    const input = JSON.stringify(defaultInputs[task], null, 2);
    const closedSource = closed ? ", provider" : "";

    switch (language) {
      case "javascript": {
        var code = `
        /*
          npm i bytez.js || yarn add bytez.js
        */

        import Bytez from "bytez.js"

        const key = "${key}"
        const sdk = new Bytez(key)

        // choose ${displayName}
        const model = sdk.model("${modelId}")

        // send input to model
        const { error, output${closedSource} } = await model.run(${input})

        console.log({ error, output${closedSource} });
      `;

        break;
      }
      case "python": {
        code = `
        """
          pip i bytez
        """

        from bytez import Bytez

        key = "${key}"
        sdk = Bytez(key)

        # choose ${displayName}
        model = sdk.model("${modelId}")

        # send input to model
        results = model.run(${input})

        print({ "error": results.error, "output": results.output })
      `;

        break;
      }
    }

    code = code?.trim().replace(/ {8}/g, "") ?? "";

    setCode(code);

    const highlight = [10, 11, 13, 14];

    for (let i = 15, j = code.split("\n").length - 1; i < j; i++) {
      highlight.push(i);
    }

    setHighlight(highlight);
  }, [user.key, model.node, language]);

  return [code, highlight];
}
